import { EmployeeDepartment, SiteRole } from '../auth/types';

/**
 * Platform admin identity — separate from tenant users.
 *
 * Document ID = Firebase Auth UID (O(1) lookups).
 * The same UID can exist in both `admins` and `users` collections
 * (independent identity planes).
 */
export interface IAdmin {
  /** Firebase Auth UID (also the Firestore document ID) */
  readonly uid: string;
  readonly email: string;
  readonly displayName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly photoURL?: string | null;

  /** Platform role — required for every admin */
  readonly siteRole: SiteRole;
  readonly isActive: boolean;

  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly lastLogin?: Date;

  /** UID of the admin who created this record */
  readonly createdBy?: string;
  /** Internal notes (e.g. "CTO", "Support contractor") */
  readonly notes?: string;

  /** Organizational department — orthogonal to siteRole (which gates permissions) */
  readonly department?: EmployeeDepartment;
  /** Geographic scope for regional managers (e.g. ['CT', 'NY']) */
  readonly regionScope?: string[];
}

export class Admin implements IAdmin {
  readonly uid: string;
  readonly email: string;
  readonly displayName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly photoURL?: string | null;
  readonly siteRole: SiteRole;
  readonly isActive: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly lastLogin?: Date;
  readonly createdBy?: string;
  readonly notes?: string;
  readonly department?: EmployeeDepartment;
  readonly regionScope?: string[];

  constructor(data: IAdmin) {
    this.uid = data.uid;
    this.email = data.email;
    this.displayName = data.displayName;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.photoURL = data.photoURL;
    this.siteRole = data.siteRole;
    this.isActive = data.isActive ?? true;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
    this.lastLogin = data.lastLogin;
    this.createdBy = data.createdBy;
    this.notes = data.notes;
    this.department = data.department;
    this.regionScope = data.regionScope;
  }

  toJSON(): IAdmin {
    return {
      uid: this.uid,
      email: this.email,
      displayName: this.displayName,
      firstName: this.firstName,
      lastName: this.lastName,
      photoURL: this.photoURL,
      siteRole: this.siteRole,
      isActive: this.isActive,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      lastLogin: this.lastLogin,
      createdBy: this.createdBy,
      notes: this.notes,
      department: this.department,
      regionScope: this.regionScope,
    };
  }

  static fromJSON(data: Record<string, any>): Admin {
    if (!data.uid || typeof data.uid !== 'string') throw new Error('Admin.fromJSON: UID is missing or invalid.');
    if (!data.email || typeof data.email !== 'string') throw new Error('Admin.fromJSON: Email is missing or invalid.');
    if (!data.siteRole || typeof data.siteRole !== 'string') throw new Error('Admin.fromJSON: siteRole is missing or invalid.');

    const toDate = (dateValue: any): Date | undefined => {
      if (!dateValue) return undefined;
      if (dateValue.toDate) return dateValue.toDate(); // Firestore Timestamp
      if (typeof dateValue === 'string') return new Date(dateValue);
      return dateValue as Date;
    };

    return new Admin({
      uid: data.uid,
      email: data.email,
      displayName: data.displayName as string | undefined,
      firstName: data.firstName as string | undefined,
      lastName: data.lastName as string | undefined,
      photoURL: data.photoURL as string | null | undefined,
      siteRole: data.siteRole as SiteRole,
      isActive: data.isActive as boolean ?? true,
      createdAt: toDate(data.createdAt) || new Date(),
      updatedAt: toDate(data.updatedAt) || new Date(),
      lastLogin: toDate(data.lastLogin),
      createdBy: data.createdBy as string | undefined,
      notes: data.notes as string | undefined,
      department: data.department as EmployeeDepartment | undefined,
      regionScope: data.regionScope as string[] | undefined,
    });
  }
}
